import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Grid } from '@mui/material';
import { LONG_DATE_FORMAT, stringifyJsonData } from '@npi/buddy-utils';

import { MetaTags } from '@redwoodjs/web';

import TableList from 'src/components/TableList/TableList';
import { showErrorToast } from 'src/components/Toasts/toast';
import { AUDIT_LOGS_CSV_TABLE_HEADERS, AUDIT_LOGS_TABLE_HEADERS } from 'src/constants';
import { GET_AUDIT_LOGS } from 'src/graphql/queries/auditLogs.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import {
    filteredAuditLogsTableData,
    FilterOptionsType,
    updateTableFilters,
} from 'src/utils/tableUtils';
import { formatDate } from 'src/utils/utils';
import './AuditLogPageStyles.css';

const AuditLogPage = () => {
    const [tableFilters, setTableFilters] = useState({});

    const { data, loading } = useQuery(GET_AUDIT_LOGS, {
        fetchPolicy: 'cache-and-network',
        onError: () => {
            return showErrorToast('There was an error retrieving the audit logs');
        },
    });

    function formatDetails(details) {
        if (typeof details === 'object' && details !== null) {
            return `{${Object.entries(details)
                .map(([key, value]) => {
                    return `${key}: ${value}`;
                })
                .join(', ')}}`;
        } else if (typeof details === 'string') {
            return details.replace(/"/g, '').trim();
        }
        return details;
    }

    const tableData = useMemo(() => {
        const logs = data?.auditLogs ?? [];

        return filteredAuditLogsTableData(logs, tableFilters).map(
            ({ createdAt, user, details, ipAddress, ...rest }) => {
                const userFullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
                const hierarchyLevel = user?.role?.hierarchyLevel ?? '';

                return {
                    ...rest,
                    ipAddress,
                    user: userFullName,
                    hierarchyLevel,
                    details: stringifyJsonData(details),
                    createdAt: formatDate({ date: createdAt, format: LONG_DATE_FORMAT }),
                };
            }
        );
    }, [data, tableFilters]);

    const filterOptions = () => {
        const auditLogs = data?.auditLogs ?? [];
        return Object.fromEntries([
            [
                'User',
                Array.from(
                    new Set(
                        auditLogs.map(({ user }) => {
                            return `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
                        })
                    )
                )
                    .filter((fullName) => {
                        return fullName;
                    })
                    .map((fullName) => {
                        return {
                            value: fullName,
                            label: fullName,
                        };
                    }),
            ],
            [
                'Hierarchy Level',
                Array.from(
                    new Set(
                        auditLogs.map(({ user }) => {
                            return user?.role?.hierarchyLevel;
                        })
                    )
                )
                    .filter((hierarchyLevel) => {
                        return hierarchyLevel;
                    })
                    .map((hierarchyLevel) => {
                        return {
                            value: hierarchyLevel,
                            label: hierarchyLevel,
                        };
                    }),
            ],
            ['Date', []],
            [
                'Event Type',
                Array.from(
                    new Set(
                        auditLogs.map(({ event }) => {
                            return event;
                        })
                    )
                )
                    .filter((eventType) => {
                        return eventType;
                    })
                    .map((eventType: string) => {
                        return {
                            value: eventType,
                            label: eventType,
                        };
                    }),
            ],
        ]) as FilterOptionsType;
    };

    return (
        <PageContent heading="Audit Log" subHeading="View and Download NPI Audit Log as necessary">
            <MetaTags title="Audit Log" description="Audit Log" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    <TableList
                        data={tableData}
                        headers={AUDIT_LOGS_TABLE_HEADERS}
                        csvHeaders={AUDIT_LOGS_CSV_TABLE_HEADERS}
                        title="Audit Logs Lists"
                        isAuditLogTable
                        enableFilter
                        enableExport
                        filterOptions={filterOptions()}
                        updateTableFilters={(filter: string, newFilterValue: string) => {
                            return setTableFilters((prevFilters) => {
                                return updateTableFilters(prevFilters, newFilterValue, filter);
                            });
                        }}
                        enableActions={false}
                    />
                </Grid>
            </Grid>
        </PageContent>
    );
};

export default AuditLogPage;
